import { Component, OnInit, ChangeDetectionStrategy, Input, Output, OnChanges, OnDestroy, SimpleChanges, EventEmitter } from '@angular/core';
import { run } from 'formula';
import { isEqual } from 'lodash';
import { InterpolateService } from '@ispm/shared/services/interpolate.service';
import { FormGroup, FormControl } from '@angular/forms';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

const DEFAULT_OUTPUT_KEY = '';

/*
 * Derived field capable of calculating results based on other form values.
 *
 * Example usage in dynamic form:
 *
 * ```
 * {
 *   "name": "fieldName",
 *   "type": "derived",
 *   "label": "fieldLabel",
 *   "widgetPayload": {
 *     "formula": "$(otherField1) + $(otherField2)",
 *     "displayFallback": "-",
 *     "outputTransform": {
 *       "": {"code": "0", "display": "Default"},
 *       "1": {"code": "val1", "display": "Value one"},
 *       "2": {"code": "val2", "display": "Value two"}
 *     }
 *   },
 *   "required": false,
 *   "displayPattern": "$(fieldName) ($(otherField1) + $(otherField2))"
 * }
 * ```
 *
 * The field configuration allows to define how the field works with the data. Configuration widgetPayload:
 * -  `displayPattern`: (required) Pattern that displays the output of the formula, to show raw output use: `$(< your field name >)`.
 * -  `widgetPayload.formula`: (required) String that is parsed to an executable formula, retrieve field values with `$(< name of other field >)`.
 *                             The formula can use the methods listed: https://formula.github.io/formula/docs/code/src/functions.
 * -  `widgetPayload.displayFallback`: Value that is shown in case of formula failure, or when no value could be calculated.
 * -  `widgetPayload.outputTransform`: Transforms output values to other data, for example to a certain string or Valueset item.
 *                                     An outputTransform with an empty string represents the fallback case in case no other
 *                                     transform has matched the output.
 */
@Component({
  selector: 'gen-derived-field',
  templateUrl: './derived-field.component.html',
  styleUrls: ['./derived-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class DerivedFieldComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public widgetPayload: { [key: string]: any };
  @Input() public value: any;
  @Input() public formInstance: FormGroup;
  @Input() public displayPattern: string;
  @Input() public formControl: FormControl;
  @Output() change: EventEmitter<any> = new EventEmitter();

  public displayValue = '';

  private formula: string;
  private displayFallback: string;
  private outputTransform: { [key: string]: any };
  private destroyer: Subject<any> = new Subject();

  constructor(private interpolator: InterpolateService) { }

  public ngOnInit(): void {
    this.update();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    let updateValue = false;
    if (changes.widgetPayload) {
      this.formula = this.widgetPayload.formula;
      this.displayFallback = this.widgetPayload.displayFallback;
      this.outputTransform = this.widgetPayload.outputTransform;
    }
    if (changes.formInstance) {
      this.formInstance.valueChanges.pipe(
        takeUntil(this.destroyer),
        distinctUntilChanged((x, y) => isEqual(x, y))
      ).subscribe(() => this.update());
      updateValue = true;
    }
    if (changes.value || updateValue) {
      this.update();
    }
  }

  public ngOnDestroy(): void {
    this.destroyer.next();
    this.destroyer.complete();
  }

  private update(): void {
    if (!this.formInstance || !this.formula || !this.displayPattern) { return; }

    const value = this.getFormulaResult();
    this.displayValue = this.interpolator.interpolate(this.displayPattern, this.formInstance.getRawValue(), true);
    if (!isEqual(value, this.value) || !isEqual(value, this.displayValue)) {
      this.value = value;
      this.emitUpdate();
    }

    if (this.value === null && this.displayFallback !== undefined) {
      this.displayValue = this.displayFallback;
    } else {
      this.displayValue = this.interpolator.interpolate(this.displayPattern, this.formInstance.getRawValue(), true);
    }
    if (this.displayValue == 'Unknown') {
      this.displayValue = 'Default_Unknown'
    }
  }

  /**
   * Executes widget formula on interpolated formula string
   */
  private getFormulaResult(): any {
    let calculatedValue = null;

    try {
      calculatedValue = run(this.interpolator.interpolate(this.formula, this.formInstance.getRawValue(), true), { });
      if (calculatedValue instanceof Error) {
        calculatedValue = null;
      }
      calculatedValue = this.getTransformedValue(calculatedValue);
    } catch (exception) {
      if (this.outputTransform && (this.outputTransform[DEFAULT_OUTPUT_KEY] || this.outputTransform[DEFAULT_OUTPUT_KEY] === 0 )) {
        calculatedValue = this.outputTransform[DEFAULT_OUTPUT_KEY];
      }
    }

    return calculatedValue;
  }

  /**
   * Transform formula output to predifined configuration output value
   */
  private getTransformedValue(value: (number | null)): any {
    if (!this.outputTransform) { return value; }

    if (this.outputTransform[String(value)]) {
      return this.outputTransform[String(value)];
    } else if (this.outputTransform[DEFAULT_OUTPUT_KEY]) {
      return this.outputTransform[DEFAULT_OUTPUT_KEY];
    }
    return null;
  }

  private emitUpdate(): void {
    if (this.formControl) {
      this.formControl.patchValue(this.value);
    }
    this.change.emit(this.value);
  }
}
